import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  cancelDeliveryRun,
  pauseDeliveryRun,
  resumeDeliveryRun,
  retryFailedDeliveryRun,
} from '../services/delivery-runs.service';
import type { RetryFailedResponse } from '../types/delivery-runs.types';

/**
 * Shared post-action refetch: invalidate the whole `deliveries` cache so the
 * run detail, tree, products, insights, and the list all re-read reconciled
 * state. Runs on settle so a benign 404/409 still refreshes rather than leaving
 * the view stale.
 */
function useInvalidateDeliveries() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.deliveries.all });
}

/**
 * Shared control-failure handler. Controls act on the *live* Temporal workflow,
 * so some failures are expected rather than faults: `404` = the run closed
 * between render and click (the settle refetch already corrects the view); `409`
 * = the action isn't available for this run/sub-variant (e.g. a feed run can't
 * pause, or there are no failures to retry); `422` = the re-dispatch failed
 * validation. The server message is surfaced for the last two.
 */
function useControlError() {
  const { t } = useTranslation('deliveries');
  return (err: unknown) => {
    const status = err instanceof ApiError ? err.statusCode : null;
    if (status === 404) {
      toast.info(t('controls.toast.alreadyClosed'));
      return;
    }
    if (status === 409) {
      toast.warning(extractApiError(err, t('controls.toast.notAvailable')));
      return;
    }
    if (status === 422) {
      toast.warning(extractApiError(err, t('controls.toast.invalid')));
      return;
    }
    toast.error(extractApiError(err, t('controls.toast.error')));
  };
}

/**
 * Success copy for a retry: "Retrying N products · M skipped (permanent errors …)"
 * when the server reports counts (skipped only when > 0), else the generic line.
 */
function retryToastMessage(t: TFunction<'deliveries'>, data: RetryFailedResponse): string {
  const { retriedCount, skippedNonRetryableCount: skipped = 0 } = data;
  if (retriedCount === undefined) return t('controls.toast.retryQueued');
  return skipped > 0
    ? t('controls.toast.retryQueuedCountSkipped', { count: retriedCount, skipped })
    : t('controls.toast.retryQueuedCount', { count: retriedCount });
}

/** Pause a run at the next page boundary. Offer it only when `deriveDeliveryActions(run).canPause`
 *  (the run's `availableActions`), not the static `capabilities` matrix. */
export function usePauseDeliveryRun(runId: string) {
  const { t } = useTranslation('deliveries');
  const invalidate = useInvalidateDeliveries();
  const onError = useControlError();
  return useMutation({
    mutationFn: () => pauseDeliveryRun(runId),
    onSuccess: () => toast.success(t('controls.toast.paused')),
    onError,
    onSettled: () => void invalidate(),
  });
}

/** Resume a paused run. Offer it only when `deriveDeliveryActions(run).canResume`. */
export function useResumeDeliveryRun(runId: string) {
  const { t } = useTranslation('deliveries');
  const invalidate = useInvalidateDeliveries();
  const onError = useControlError();
  return useMutation({
    mutationFn: () => resumeDeliveryRun(runId),
    onSuccess: () => toast.success(t('controls.toast.resumed')),
    onError,
    onSettled: () => void invalidate(),
  });
}

/** Cancel a run. Offer it only when `deriveDeliveryActions(run).canCancel`. */
export function useCancelDeliveryRun(runId: string) {
  const { t } = useTranslation('deliveries');
  const invalidate = useInvalidateDeliveries();
  const onError = useControlError();
  return useMutation({
    mutationFn: () => cancelDeliveryRun(runId),
    onSuccess: () => toast.success(t('controls.toast.cancelled')),
    onError,
    onSettled: () => void invalidate(),
  });
}

/**
 * Retry only the failed products into a NEW run linked to this one. The caller
 * may pass its own `onSuccess` to `mutate` (e.g. to open `newRunId`); this hook
 * toasts — with the retried / permanently-skipped counts when the server sends
 * them — and invalidates. Offer it only when `deriveDeliveryActions(run).canRetryFailed`
 * (`RETRY_FAILED` in `availableActions`, which drops once the retry budget is spent);
 * a 409 (budget spent, or every failure is permanent) surfaces the server's message.
 */
export function useRetryFailedDeliveryRun(runId: string) {
  const { t } = useTranslation('deliveries');
  const invalidate = useInvalidateDeliveries();
  const onError = useControlError();
  return useMutation({
    mutationFn: () => retryFailedDeliveryRun(runId),
    onSuccess: (data) => toast.success(retryToastMessage(t, data)),
    onError,
    onSettled: () => void invalidate(),
  });
}
